/*
 * Scenario: delete-multi
 *
 * Drei Delete-Vorschlaege auf verschiedene Paragraphen, verteilt ueber das
 * Dokument. Testet Multi-Accept/Reject-Reihenfolge im ReviewController und
 * stellt sicher, dass _afterSuggestionChange auch bei mehreren Deletes
 * korrekt zwischen pending-Vorschlaegen springt.
 *
 * Zielindizes 1, 3, 5 in textParagraphs — ueberspringt jeweils einen Para,
 * damit das visuelle Muster auseinander gezogen ist.
 */

import { emit, wait } from './_helpers.js'

export const name = 'delete-multi'
export const description = 'Three delete proposals across the document.'

const TARGET_INDICES = [1, 3, 5]
const REASONS = [
    'Recaps the previous beat — no new information.',
    'Stalls the pacing; the scene already landed.',
    'Tonally off — pulls the reader out of the moment.'
]

export async function run(state) {
    await emit(state, 'session', { sessionId: 'mock-delete-multi' }, 0)
    await emit(state, 'thinking', { text: 'Three paragraphs feel like ballast — let me show you which.' }, 250)
    await emit(state, 'thinkingDone', {}, 200)

    const paras = state.context?.cursorContext?.textParagraphs || []
    const usableTargets = TARGET_INDICES.filter(i => i < paras.length)
    if (usableTargets.length === 0) {
        await emit(state, 'delta', { text: 'Document too short for delete-multi.' }, 100)
        await emit(state, 'done', { usage: {} }, 100)
        return
    }

    const toolCallIds = []
    for (let i = 0; i < usableTargets.length; i++) {
        const target = paras[usableTargets[i]]
        const toolCallId = `tu_dm_${i}_${Date.now().toString(36)}`
        toolCallIds.push(toolCallId)
        await emit(state, 'proposeDelete', {
            toolCallId,
            paragraphId: target.id,
            baseVersion: typeof target.version === 'number' ? target.version : undefined,
            reason: REASONS[i] || 'Redundant — recommend cutting.'
        }, 250)
    }
    await emit(state, 'done', { usage: {} }, 200)

    let applied = 0, rejected = 0
    try {
        const results = await Promise.all(
            toolCallIds.map(id => wait(state, id, 5 * 60 * 1000))
        )
        for (const info of results) {
            if (info.result === 'applied') applied++
            else rejected++
        }
        const reaction = applied === toolCallIds.length
            ? 'All cuts accepted — pacing should feel tighter.'
            : applied === 0
                ? 'Okay, kept all three. You know the rhythm better than me.'
                : `${applied} cut, ${rejected} kept. Good.`
        await emit(state, 'delta', { text: reaction }, 300)
        await emit(state, 'done', { usage: {} }, 100)
    } catch { /* abort */ }
}
